import { PersonAddresses } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/PersonAddress/PersonAddresses.tsx';
import { Divider } from '@mantine/core';
import React, { useContext, useMemo, useState } from 'react';
import { PermissionRole, PersonType } from '@dealstack/shared/lib/generated';
import {
	usePermissionRequest,
	PermissionWrapper,
} from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import {
	FGAInternalRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions.ts';
import { defaultToEmptyString } from '@dealstack/shared/lib/format/string.ts';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { AddressDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/PersonAddress/AddressDrawer/AddressDrawer.tsx';
import { FloatingDetailsActions } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/FloatingDetailsActions.tsx';
import { RegistrationDetailsSection } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/LegalPersonDetails/component/RegistrationDetailsSection.tsx';
import { PersonClassifierSection } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/LegalPersonDetails/component/PersonClassifierSection.tsx';
import { RelatedPeopleDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/RelatedPeopleDrawer/RelatedPeopleDrawer.tsx';
import { RelatedPeopleSection } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/RelatedPeopleSection/RelatedPeopleSection.tsx';
import { BankAccountSection } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/BankAccountSection/BankAccountSection.tsx';
import { BankAccountDrawer } from '@dealstack/frontend/shared/components/BankAccountDrawer/BankAccountDrawer.tsx';
import { useCapBankAccountDrawerProps } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/hooks/useCapBankAccountDrawerProps.ts';
import { TaxInformationSection } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/TaxInformationSection/TaxInformationSection.tsx';
import { TaxInfoDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/TaxInfoDrawer/TaxInfoDrawer.tsx';
import { InvestmentProgrammeSection } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/InvestmentProgrammeSection/InvestmentProgrammeSection.tsx';
import { FundPersonDealLinkSection } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/FundPersonDealLinkSection/FundPersonDealLinkSection.tsx';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag.ts';

export const LegalPersonDetails = () => {
	const { dealId, deal } = useContext(DealContext) || {};
	const bankAccountDrawerProps = useCapBankAccountDrawerProps();

	const [createAddressMode, setCreateAddressMode] = useState<boolean>(false);
	const [createRelatedPeopleMode, setCreateRelatedPeopleMode] = useState<boolean>(false);
	const [createBankAccountMode, setCreateBankAccountMode] = useState<boolean>(false);
	const [createTaxInfoMode, setCreateTaxInfoMode] = useState<boolean>(false);
	const [addLinkedDeal, setAddLinkedDeal] = useState<boolean>(false);

	const { value: fundsEnabled } = useDealstackFeatureFlag(FEATURE_FLAG.FUNDS);

	const { hasPermission: hasSupportPermission } = usePermissionRequest({
		resourceType: ResourceType.DEAL,
		resourceId: defaultToEmptyString(dealId),
		permissionRole: FGAInternalRole.DEALSTACK_SUPPORT,
	});

	const allowFundPersonDealLinking = useMemo(
		() => hasSupportPermission && deal?.fundId && fundsEnabled,
		[hasSupportPermission, deal, fundsEnabled]
	);

	return (
		<PermissionWrapper
			resourceType={ResourceType.DEAL}
			resourceId={defaultToEmptyString(dealId)}
			permissionRole={PermissionRole.EDITOR}
		>
			{(hasEditorPermission) => (
				<>
					<RegistrationDetailsSection hasEditorPermission={hasEditorPermission} />

					<PersonClassifierSection hasEditorPermission={hasEditorPermission} />

					<PersonAddresses
						hasEditorPermission={hasEditorPermission}
						onAddAddress={() => setCreateAddressMode(true)}
					/>

					<BankAccountSection hasEditorPermission={hasEditorPermission} />

					<TaxInformationSection hasEditorPermission={hasEditorPermission} />

					<RelatedPeopleSection hasEditorPermission={hasEditorPermission} />

					<InvestmentProgrammeSection hasEditorPermission={hasEditorPermission} />

					{allowFundPersonDealLinking && (
						<FundPersonDealLinkSection
							linkDrawerOpen={addLinkedDeal}
							onCloseLinkDrawer={() => setAddLinkedDeal(false)}
							openLinkDrawer={() => setAddLinkedDeal(true)}
						/>
					)}

					<Divider />

					<AddressDrawer
						opened={createAddressMode}
						onClose={() => setCreateAddressMode(false)}
						hasPermission={hasEditorPermission}
						address={null}
					/>

					<RelatedPeopleDrawer
						opened={createRelatedPeopleMode}
						onClose={() => setCreateRelatedPeopleMode(false)}
						selectedEntityType={PersonType.LEGAL}
						hasPermission={hasEditorPermission}
						relationType={null}
						relatedPeople={null}
					/>

					<BankAccountDrawer
						{...bankAccountDrawerProps}
						opened={createBankAccountMode}
						onClose={() => setCreateBankAccountMode(false)}
						hasPermission={hasEditorPermission}
						bankAccount={null}
					/>

					<TaxInfoDrawer
						opened={createTaxInfoMode}
						onClose={() => setCreateTaxInfoMode(false)}
						hasPermission={hasEditorPermission}
						taxData={null}
					/>

					{hasEditorPermission ? (
						<FloatingDetailsActions
							onAddAddress={() => setCreateAddressMode(true)}
							onAddRelatedPerson={() => setCreateRelatedPeopleMode(true)}
							onAddBankAccount={() => setCreateBankAccountMode(true)}
							onAddTaxInformation={() => setCreateTaxInfoMode(true)}
							onAddLinkedDeal={
								allowFundPersonDealLinking
									? () => setAddLinkedDeal(true)
									: undefined
							}
						/>
					) : null}
				</>
			)}
		</PermissionWrapper>
	);
};
